import { useQuery } from '@tanstack/react-query';
import type { ReminderRule, ReminderRun } from '@kaba/shared';
import { api } from './api';
import { queryClient } from './board';

export const cardRemindersKey = (cardId: string) => ['reminders', 'card', cardId] as const;
export const boardRemindersKey = (boardId: string) => ['reminders', 'board', boardId] as const;

export function useCardReminders(cardId: string, enabled = true) {
  return useQuery({ queryKey: cardRemindersKey(cardId), queryFn: () => api.get<ReminderRule[]>(`/api/cards/${cardId}/reminders`), enabled });
}

export function useBoardReminders(boardId: string, enabled = true) {
  return useQuery({ queryKey: boardRemindersKey(boardId), queryFn: () => api.get<ReminderRule[]>(`/api/boards/${boardId}/reminders`), enabled });
}

export function useReminderRuns(ruleId: string, cardId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['reminder-runs', ruleId, cardId],
    queryFn: () => api.get<ReminderRun[]>(`/api/reminders/${ruleId}/runs${cardId ? `?cardId=${cardId}` : ''}`),
    enabled,
  });
}

export interface ReminderTargets {
  users: { id: string; name: string }[];
  groups: { id: string; name: string }[];
  /** Namen aller in Regeln genannten, noch vorhandenen Gruppen (auch für Externe) */
  named?: { id: string; name: string }[];
}

export function useReminderTargets(boardId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['reminder-targets', boardId],
    queryFn: () => api.get<ReminderTargets>(`/api/boards/${boardId}/reminder-targets`),
    enabled,
    staleTime: 60_000,
  });
}

export function refreshReminders() {
  void queryClient.invalidateQueries({ queryKey: ['reminders'] });
  void queryClient.invalidateQueries({ queryKey: ['reminder-runs'] });
}

/** „Für mich später erinnern“: persönliche Wiedervorlage (K03) */
export async function snooze(cardId: string, at: Date, text?: string) {
  await api.post(`/api/cards/${cardId}/snooze`, { at: at.toISOString(), text: text ?? null });
  refreshReminders();
}

/** Vorschläge für die Wiedervorlage: in 1 Stunde, morgen 9:00, nächsten Montag 9:00 */
export function snoozeChoices(now = new Date()): { label: string; at: Date }[] {
  const inHour = new Date(now.getTime() + 3600_000);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  const monday = new Date(now);
  monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
  monday.setHours(9, 0, 0, 0);
  return [
    { label: 'In 1 Stunde', at: inHour },
    { label: 'Morgen, 9:00', at: tomorrow },
    { label: 'Nächsten Montag, 9:00', at: monday },
  ];
}

const fmt = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
export const formatWhen = (iso: string) => fmt.format(new Date(iso));
